import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, of, type Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  type JobDetailsApiResponse,
  type JobDetailsGetResult,
  type VendorApproveEstimateApiResponse,
  type VendorApproveEstimateRequest,
  type VendorApproveEstimateResult,
  type VendorPortalEstimateEditApiResponse,
  type VendorPortalEstimateEditRequest,
  type VendorPortalEstimateEditResult,
  type VendorPortalMultiOptionEditRequest,
} from './job-details.types';

function jobDetailsResultFromEnvelope(res: JobDetailsApiResponse): JobDetailsGetResult {
  if (res.status && res.data) {
    return { outcome: 'success', data: res.data };
  }
  const msg = res.message?.trim();
  if (msg) {
    return { outcome: 'apiMessage', message: msg, responseCode: res.responseCode };
  }
  return { outcome: 'failure' };
}

/** When the server uses HTTP 4xx/5xx, Angular errors before `map`; the JSON body is still here. */
function jobDetailsResultFromHttpError(error: unknown): JobDetailsGetResult | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return null;
    }
  }
  if (!body || typeof body !== 'object') {
    return null;
  }
  const o = body as Partial<JobDetailsApiResponse>;
  if (typeof o.message !== 'string' || !o.message.trim()) {
    return null;
  }
  const envelope: JobDetailsApiResponse = {
    status: Boolean(o.status),
    responseCode: typeof o.responseCode === 'number' ? o.responseCode : error.status,
    message: o.message,
    data: (o.data ?? null) as JobDetailsApiResponse['data'],
    details: Array.isArray(o.details) ? o.details : [],
    unixTime: typeof o.unixTime === 'number' ? o.unixTime : 0,
    traceId: typeof o.traceId === 'string' ? o.traceId : '',
  };
  return jobDetailsResultFromEnvelope(envelope);
}

@Injectable({ providedIn: 'root' })
export class JobDetailsService {
  private readonly http = inject(HttpClient);

  getJobDetails(jobKey: string, vendorKey: string): Observable<JobDetailsGetResult> {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/job/details`;
    const params = new HttpParams().set('jobKey', jobKey).set('vendorKey', vendorKey);

    return this.http.get<JobDetailsApiResponse>(path, { params }).pipe(
      map((res) => jobDetailsResultFromEnvelope(res)),
      catchError((err): Observable<JobDetailsGetResult> => {
        const fromBody = jobDetailsResultFromHttpError(err);
        if (fromBody && fromBody.outcome !== 'failure') {
          return of(fromBody);
        }
        return of({ outcome: 'failure' });
      }),
    );
  }

  editEstimate(request: VendorPortalEstimateEditRequest): Observable<VendorPortalEstimateEditResult> {
    return this.postEstimateEdit('estimates/edit', request);
  }

  /** Multiple-option estimate: every option's lines saved in one batch (one transaction on the server). */
  editMultiOptionEstimate(
    request: VendorPortalMultiOptionEditRequest,
  ): Observable<VendorPortalEstimateEditResult> {
    return this.postEstimateEdit('estimates/edit-batch', request);
  }

  private postEstimateEdit(
    route: string,
    request: VendorPortalEstimateEditRequest | VendorPortalMultiOptionEditRequest,
  ): Observable<VendorPortalEstimateEditResult> {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/job/${route}`;

    return this.http.post<VendorPortalEstimateEditApiResponse>(path, request).pipe(
      map((res): VendorPortalEstimateEditResult =>
        res.status
          ? { outcome: 'success', message: res.message }
          : { outcome: 'apiMessage', message: res.message, responseCode: res.responseCode },
      ),
      catchError((err): Observable<VendorPortalEstimateEditResult> => {
        if (err instanceof HttpErrorResponse) {
          let body: unknown = err.error;
          if (typeof body === 'string') {
            try {
              body = JSON.parse(body) as unknown;
            } catch {
              body = null;
            }
          }
          const o = (body ?? {}) as Partial<VendorPortalEstimateEditApiResponse>;
          if (typeof o.message === 'string' && o.message.trim()) {
            return of({ outcome: 'apiMessage', message: o.message, responseCode: err.status });
          }
        }
        return of({ outcome: 'failure' });
      }),
    );
  }

  approveEstimate(
    estimateKey: string,
    request: VendorApproveEstimateRequest,
  ): Observable<VendorApproveEstimateResult> {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/job/estimates/${estimateKey}/approve`;

    return this.http.post<VendorApproveEstimateApiResponse>(path, request).pipe(
      map((res): VendorApproveEstimateResult =>
        res.status
          ? { outcome: 'success', message: res.message }
          : { outcome: 'apiMessage', message: res.message, responseCode: res.responseCode },
      ),
      catchError((err): Observable<VendorApproveEstimateResult> => {
        if (err instanceof HttpErrorResponse) {
          let body: unknown = err.error;
          if (typeof body === 'string') {
            try {
              body = JSON.parse(body) as unknown;
            } catch {
              body = null;
            }
          }
          const o = (body ?? {}) as Partial<VendorApproveEstimateApiResponse>;
          if (typeof o.message === 'string' && o.message.trim()) {
            return of({ outcome: 'apiMessage', message: o.message, responseCode: err.status });
          }
        }
        return of({ outcome: 'failure' });
      }),
    );
  }
}
